import React, { useState, useEffect } from 'react';
import { Modal, Button, Input, Radio, Skeleton, Empty, Tag, Spin, message, Flex } from 'antd';
import {
    ThunderboltOutlined,
    MessageOutlined,
    CheckCircleFilled,
    RocketOutlined,
    ArrowRightOutlined,
    ArrowLeftOutlined,
    CommentOutlined,
    SendOutlined,
    StarFilled,
    FireFilled,
    PlusOutlined,
    CloseOutlined
} from '@ant-design/icons';
import callAPI from '../fetch/main';
import confetti from 'canvas-confetti';
import './FirstAutomationOnboarding.css';

const FirstAutomationOnboarding = ({
    open,
    onClose,
    onComplete,
    onSetupAccount,
    decodedToken
}) => {
    // Step states
    const [currentStep, setCurrentStep] = useState(0);

    // Posts loading
    const [loading, setLoading] = useState(false);
    const [posts, setPosts] = useState([]);
    const [noPosts, setNoPosts] = useState(false); // Track if user has no posts

    // User selections
    const [selectedPost, setSelectedPost] = useState(null);
    const [triggerType, setTriggerType] = useState('all'); // 'all' or 'specific'
    const [triggerWords, setTriggerWords] = useState([]); // Array of trigger words
    const [triggerInput, setTriggerInput] = useState(''); // Input for adding new words
    const [dmMessage, setDmMessage] = useState('');

    // Automation creation
    const [creating, setCreating] = useState(false);
    const [automationCreated, setAutomationCreated] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);

    // Comment tracking for test screen
    const [testComments, setTestComments] = useState([]);
    const [loadingComments, setLoadingComments] = useState(false);
    const [initialCommentIds, setInitialCommentIds] = useState(null); // Track first poll
    const [pollingStartTime, setPollingStartTime] = useState(null);
    const [newSuccessDetected, setNewSuccessDetected] = useState(false);
    const [pollingStopped, setPollingStopped] = useState(false);

    // Category-based templates with trigger words and messages
    const categoryTemplates = [
        {
            id: 'coaches',
            label: 'Coaches & Educators',
            icon: '🎓',
            emoji: '📚',
            description: 'Perfect for course creators, mentors & coaches',
            triggerWords: ['join', 'pdf', 'growth', 'interested', 'ebook', 'course', 'enroll', 'learn', 'coaching', 'guide', 'free', 'book'],
            dmMessage: "Hey! 👋\n\nSo excited you're interested in leveling up! 🚀\n\nHere's the exclusive resource I promised:\n\n📚 [Your Link Here]\n\nThis will help you with your growth journey. If you have any questions, feel free to reply!\n\nLet's grow together! 💪",
            color: '#8b5cf6'
        },
        {
            id: 'ecommerce',
            label: 'Shopify & E-commerce',
            icon: '🛍️',
            emoji: '💳',
            description: 'For online stores & product sellers',
            triggerWords: ['price', 'link', 'purchase', 'buy', 'website', 'shop', 'order', 'deal', 'discount', 'sale', 'cost', 'pp', 'available', 'stock'],
            dmMessage: "Hey! 🛍️\n\nThanks for your interest in our product!\n\n🔗 Shop here: [Your Link]\n\n💳 Use code SAVE10 for 10% off your first order!\n\nLet me know if you have any questions about sizing, shipping, or anything else!",
            color: '#f59e0b'
        },
        {
            id: 'brands',
            label: 'Brands & Businesses',
            icon: '🏢',
            emoji: '✨',
            description: 'For established brands & companies',
            triggerWords: ['info', 'details', 'more', 'how', 'services', 'collab', 'partner', 'work', 'pricing', 'contact', 'quote', 'booking', 'appointment'],
            dmMessage: "Hey! ✨\n\nThank you for reaching out!\n\nWe'd love to share more about what we do:\n\n🔗 Learn more: [Your Link]\n📅 Book a call: [Calendar Link]\n\nFeel free to reply with any specific questions. We're here to help!",
            color: '#3b82f6'
        },
        {
            id: 'influencers',
            label: 'Content Creators',
            icon: '⭐',
            emoji: '🔗',
            description: 'For influencers & content creators',
            triggerWords: ['link', 'pdf', 'app', 'resource', 'tool', 'send', 'share', 'dm', 'recommendation', 'favorite', 'routine', 'list', 'tips'],
            dmMessage: "Hey! ⭐\n\nThanks for being part of my community! 💜\n\nHere's what I promised:\n\n🔗 Link: [Your Link]\n\nLet me know if you try it out - I'd love to hear your thoughts!\n\nMuch love! 💕",
            color: '#ec4899'
        },
        {
            id: 'ugc',
            label: 'UGC & Affiliates',
            icon: '💄',
            emoji: '🛒',
            description: 'For UGC creators & affiliate marketers',
            triggerWords: ['link', 'product', 'skin', 'lipstick', 'dress', 'buy', 'pp', 'app', 'code', 'haul', 'outfit', 'makeup', 'skincare', 'review', 'wear', 'shop'],
            dmMessage: "Hey! 💄\n\nOMG thanks for asking about the product!\n\n🛒 Get it here: [Affiliate Link]\n\n💰 Use my code: [YOUR CODE] for a special discount!\n\nLet me know what you think once you try it! 💕",
            color: '#f43f5e'
        },
        {
            id: 'offline',
            label: 'Offline / Local Stores',
            icon: '🏪',
            emoji: '📍',
            description: 'For physical stores & local businesses',
            triggerWords: ['store', 'location', 'address', 'place', 'whatsapp', 'number', 'phone', 'visit', 'shop', 'open', 'timing', 'hours', 'near', 'directions'],
            dmMessage: "Hey! 🏪\n\nThanks for reaching out! Here are our store details:\n\n📍 Location: [Your Store Address]\n\n🕐 Timings: [Mon-Sat, 10AM - 8PM]\n\n📱 WhatsApp: [Your Number]\n\nFeel free to visit us or ping on WhatsApp for any queries! See you soon! 🙌",
            color: '#10b981'
        },
        {
            id: 'others',
            label: 'Others',
            icon: '✨',
            emoji: '🔗',
            description: 'General purpose automation',
            triggerWords: ['link', 'pp', 'price', 'pdf', 'growth'],
            dmMessage: "Hey! 👋\n\nThanks for reaching out!\n\nHere's what you asked for:\n\n🔗 [Your Link Here]\n\nLet me know if you have any questions!",
            color: '#64748b'
        }
    ];

    // Simple message templates for custom option
    const messageTemplates = [
        {
            label: "Share a Link",
            value: "Hey! 👋\n\nThanks for your interest! Here's the link you requested:\n\n🔗 [Your Link Here]\n\nLet me know if you have any questions!",
            icon: "🔗"
        },
        {
            label: "Send Discount Code",
            value: "Hey! 🎉\n\nThanks for commenting! Here's your exclusive discount:\n\n💳 Code: SAVE20\n\nUse it at checkout to save 20%! Link: [Your Link]",
            icon: "🎁"
        },
        {
            label: "Lead Magnet / Freebie",
            value: "Hey! ✨\n\nExcited that you want the freebie! Here it is:\n\n📥 Download: [Your Link]\n\nEnjoy and let me know what you think!",
            icon: "📥"
        },
        {
            label: "Thank You Message",
            value: "Hey! 💜\n\nThank you so much for engaging with my content! It means a lot.\n\nFeel free to DM me anytime if you have questions!",
            icon: "💜"
        },
    ];

    // Fetch posts when modal opens
    useEffect(() => {
        if (open && posts.length === 0) {
            fetchPosts();
        }
    }, [open]);

    // Poll for comments when on Step 3 (Test screen)
    useEffect(() => {
        let intervalId;
        let timeoutId;

        if (currentStep === 3 && selectedPost?.id && !pollingStopped && !newSuccessDetected) {
            // Set polling start time on first entry
            if (!pollingStartTime) {
                setPollingStartTime(Date.now());
            }

            // Fetch immediately
            fetchComments();

            // Calculate elapsed time in minutes
            const getElapsedMinutes = () => {
                if (!pollingStartTime) return 0;
                return (Date.now() - pollingStartTime) / 60000;
            };

            // Determine polling interval based on elapsed time
            const getPollingInterval = () => {
                const elapsed = getElapsedMinutes();
                if (elapsed >= 10) {
                    return 45000; // 45 seconds after 10 mins
                }
                return 15000; // 15 seconds for first 10 mins
            };

            // Start polling
            const startPolling = () => {
                const interval = getPollingInterval();
                intervalId = setInterval(() => {
                    const elapsed = getElapsedMinutes();

                    // Stop polling after 20 minutes
                    if (elapsed >= 20) {
                        setPollingStopped(true);
                        clearInterval(intervalId);
                        return;
                    }

                    fetchComments();
                }, interval);
            };

            startPolling();

            // Check if we need to switch to slower polling after 10 mins
            const remaining = 10 * 60000 - (Date.now() - (pollingStartTime || Date.now()));
            if (remaining > 0) {
                timeoutId = setTimeout(() => {
                    clearInterval(intervalId);
                    startPolling(); // Restart with new interval
                }, remaining);
            }
        }

        return () => {
            if (intervalId) clearInterval(intervalId);
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [currentStep, selectedPost?.id, pollingStopped, newSuccessDetected, pollingStartTime]);

    // Reset polling states when leaving Step 3
    useEffect(() => {
        if (currentStep !== 3) {
            setInitialCommentIds(null);
            setPollingStartTime(null);
            setNewSuccessDetected(false);
            setPollingStopped(false);
        }
    }, [currentStep]);

    // Trigger confetti when reaching Step 4 (You're All Set)
    useEffect(() => {
        if (currentStep === 4) {
            confetti({
                particleCount: 150,
                spread: 80,
                origin: { y: 0.6 },
                colors: ['#6366f1', '#8b5cf6', '#a855f7', '#ec4899', '#10b981'],
                zIndex: 10000
            });
        }
    }, [currentStep]);

    const fetchPosts = () => {
        setLoading(true);
        callAPI({
            url: 'ig-posts',
            method: 'GET',
            loadingState: setLoading,
            dataState: (response) => {
                if (response?.posts_lists?.data) {
                    const postsData = response.posts_lists.data.slice(0, 12);
                    setPosts(postsData);
                    if (postsData.length === 0) {
                        setNoPosts(true);
                    }
                } else {
                    setNoPosts(true);
                }
            }
        });
    };

    const fetchComments = () => {
        if (!selectedPost?.id) return;

        callAPI({
            url: `ig-post-comments?post_id=${selectedPost.id}`,
            method: 'GET',
            loadingState: setLoadingComments,
            dataState: (response) => {
                if (response?.code === 200 && response?.data?.items) {
                    const items = response.data.items;

                    // First poll - store initial comment IDs
                    if (initialCommentIds === null) {
                        const ids = new Set(items.map(c => c.comment_id));
                        setInitialCommentIds(ids);
                        setTestComments(items);
                        return;
                    }

                    // Check for new comments that have been responded to
                    const newSuccessfulComments = items.filter(comment =>
                        !initialCommentIds.has(comment.comment_id) && comment.responded
                    );

                    if (newSuccessfulComments.length > 0 && !newSuccessDetected) {
                        setNewSuccessDetected(true);

                        // Trigger confetti celebration
                        confetti({
                            particleCount: 100,
                            spread: 70,
                            origin: { y: 0.6 },
                            colors: ['#10b981', '#6366f1', '#8b5cf6', '#f59e0b']
                        });

                        message.success('🎉 It worked! Your automation is working perfectly!');
                    }

                    setTestComments(items);
                }
            }
        });
    };

    const handleNext = () => {
        if (currentStep < 3) {
            setCurrentStep(currentStep + 1);
        }
    };

    const handleBack = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleCreateAutomation = () => {
        if (!selectedPost || !dmMessage.trim()) {
            message.error('Please configure your automation properly');
            return;
        }

        setCreating(true);

        // Prepare trigger words
        let triggers = [];
        let automateAll = "false";

        if (triggerType === 'all') {
            triggers = ['*allwordautomation*'];
            automateAll = "true";
        } else {
            triggers = triggerWords.filter(w => w.trim());
            if (triggers.length === 0) {
                message.error('Please enter at least one trigger word');
                setCreating(false);
                return;
            }
        }

        // Get current timestamp
        const now = new Date();
        const automated_at = now.toISOString().replace('T', ' ').slice(0, 26);

        // Create automation payload using the correct template
        const default_automation_temp = {
            sp_id: null,
            user_id: decodedToken?.user_id,
            global_automation: false,
            auto_reply: {
                buttons: [
                    {
                        title: "Learn More",
                        type: "web_url",
                        url: "https://instantdm.com"
                    }
                ],
                automate_all: automateAll,
                triger_words: triggers,
                welcome_message: {
                    enabled: false,
                    text: "Thanks for commenting",
                    button_text: "Get your link"
                },
                dms: [dmMessage],
                title: "Automated via Onboarding",
                enable_comment_reply: true,
                products: [],
                dm_template_type: "text",
                dm_reply_type: "single",
                delay: "0",
                include_ai_comment_reply: true,
                reply_comments: [
                    "Sent ✅",
                    "Check DM 📧",
                    "DMed you!"
                ],
                enable: true,
                subtitle: "",
                messages: [],
                cover_image: "",
                enable_dm_reply: true
            },
            post_id: selectedPost.id,
            shopify_linked: false,
            auto_hide: {
                enable: false,
                triger_words: [],
                ai_analysis: []
            },
            automated_at: automated_at,
            flow_editor: false,
            auto_delete: {
                enable: false,
                triger_words: [],
                ai_analysis: []
            }
        };

        callAPI({
            url: 'automate-post?demo=true',
            method: 'POST',
            body: default_automation_temp,
            loadingState: setCreating,
            dataState: (response) => {
                if (response?.code === 200) {
                    setAutomationCreated(true);
                    setCurrentStep(3);

                    // Trigger confetti celebration
                    confetti({
                        particleCount: 150,
                        spread: 80,
                        origin: { y: 0.6 },
                        colors: ['#6366f1', '#8b5cf6', '#a855f7', '#ec4899', '#f43f5e']
                    });

                    message.success('🎉 Your first automation is live!');
                } else {
                    message.error(response?.message || 'Failed to create automation');
                }
            }
        });
    };

    const renderStepIndicator = () => (
        <div className="fao-step-indicator">
            {[0, 1, 2, 3, 4].map((step) => (
                <div
                    key={step}
                    className={`fao-step-dot ${currentStep >= step ? 'active' : ''} ${currentStep === step ? 'current' : ''}`}
                />
            ))}
        </div>
    );

    const renderStep0 = () => (
        <div className="fao-step fao-step-welcome">
            {/* User Profile */}
            <div className="fao-welcome-profile">
                <img
                    src={decodedToken?.profile_picture_url || '/logo192.png'}
                    alt="Profile"
                    className="fao-welcome-avatar"
                />
            </div>
            <h2 className="fao-title">Welcome to InstantDM! 👋</h2>
            <p className="fao-welcome-username">@{decodedToken?.username || 'your_account'}</p>
            <p className="fao-subtitle">
                Let's automate your first post in just 60 seconds!
            </p>

            <div className="fao-benefits">
                <div className="fao-benefit-item">
                    <CheckCircleFilled className="fao-check-icon" />
                    <span>Boost Sales & Engagement</span>
                </div>
                <div className="fao-benefit-item">
                    <CheckCircleFilled className="fao-check-icon" />
                    <span>Save Time on DMs</span>
                </div>
                <div className="fao-benefit-item">
                    <CheckCircleFilled className="fao-check-icon" />
                    <span>Works 24/7 Automatically</span>
                </div>
            </div>

            {noPosts ? (
                <div className="fao-no-posts-cta">
                    <p style={{ color: '#666', fontSize: '14px', marginBottom: '16px', textAlign: 'center' }}>
                        📭 No posts found on your account yet. Let's set up your account first!
                    </p>
                    <Button
                        type="primary"
                        size="large"
                        className="fao-start-btn"
                        icon={<RocketOutlined />}
                        onClick={() => {
                            onComplete?.();
                            onSetupAccount?.();
                            onClose?.();
                        }}
                    >
                        Setup your Account
                    </Button>
                </div>
            ) : (
                <Button
                    type="primary"
                    size="large"
                    className="fao-start-btn"
                    icon={<ArrowRightOutlined />}
                    onClick={handleNext}
                >
                    Let's Get Started
                </Button>
            )}
        </div>
    );

    const renderStep1 = () => (
        <div className="fao-step fao-step-posts">
            <h2 className="fao-title">
                <span className="fao-step-number">1</span>
                Select a Post/Reel to Automate
            </h2>
            <p className="fao-subtitle">Choose the post you want to set up automation for</p>

            {loading ? (
                <div className="fao-posts-loading">
                    {Array(6).fill(0).map((_, idx) => (
                        <Skeleton.Image
                            key={idx}
                            style={{ width: 100, height: 100, borderRadius: 12 }}
                            active
                        />
                    ))}
                </div>
            ) : posts.length === 0 ? (
                <Empty
                    description="No posts found. Please make sure you have posts on your Instagram."
                    style={{ margin: '40px 0' }}
                />
            ) : (
                <div className="fao-posts-grid">
                    {posts.map((post) => (
                        <div
                            key={post.id}
                            className={`fao-post-item ${selectedPost?.id === post.id ? 'selected' : ''}`}
                            onClick={() => setSelectedPost(post)}
                        >
                            <img
                                src={post.thumbnail_url || post.media_url || post.full_picture}
                                alt="Post"
                                onError={(e) => {
                                    e.target.src = '/placeholder-post.png';
                                }}
                            />
                            {selectedPost?.id === post.id && (
                                <div className="fao-post-selected-overlay">
                                    <CheckCircleFilled />
                                </div>
                            )}
                            {post.media_type === 'VIDEO' && (
                                <div className="fao-post-video-badge">▶ REEL</div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    const handleCategorySelect = (category) => {
        setSelectedCategory(category.id);
        setTriggerType('specific');
        setTriggerWords([...category.triggerWords]); // Use array
        // Don't prefill DM message - let user add their own
    };

    // Add a new trigger word
    const addTriggerWord = () => {
        const word = triggerInput.trim().toLowerCase();
        if (word && !triggerWords.includes(word)) {
            setTriggerWords([...triggerWords, word]);
            setTriggerInput('');
        } else if (triggerWords.includes(word)) {
            message.warning('This word is already added');
            setTriggerInput('');
        }
    };

    // Remove a trigger word
    const removeTriggerWord = (wordToRemove) => {
        setTriggerWords(triggerWords.filter(word => word !== wordToRemove));
    };

    // Handle Enter key in input
    const handleTriggerInputKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            addTriggerWord();
        }
    };

    const renderStep2 = () => (
        <div className="fao-step fao-step-config">
            <h2 className="fao-title">
                <span className="fao-step-number">2</span>
                Configure Your Automation
            </h2>

            {/* Category Templates Section */}
            <div className="fao-config-section">
                <h4>🎯 What's your niche? <span style={{ fontWeight: 400, fontSize: '12px', color: '#888' }}>(Quick Setup)</span></h4>
                <div className="fao-category-grid">
                    {categoryTemplates.map((category) => (
                        <div
                            key={category.id}
                            className={`fao-category-card ${selectedCategory === category.id ? 'selected' : ''}`}
                            onClick={() => handleCategorySelect(category)}
                            style={{
                                '--category-color': category.color,
                                borderColor: selectedCategory === category.id ? category.color : undefined
                            }}
                        >
                            <div className="fao-category-icon">{category.icon}</div>
                            <div className="fao-category-info">
                                <div className="fao-category-label">{category.label}</div>
                                <div className="fao-category-desc">{category.description}</div>
                            </div>
                            {selectedCategory === category.id && (
                                <CheckCircleFilled className="fao-category-check" style={{ color: category.color }} />
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Trigger Words Section */}
            <div className="fao-config-section">
                <h4><CommentOutlined /> Trigger Words</h4>
                <div className="fao-trigger-type-row">
                    <Radio.Group
                        value={triggerType}
                        onChange={(e) => {
                            setTriggerType(e.target.value);
                            if (e.target.value === 'all') {
                                setSelectedCategory(null);
                            }
                        }}
                        size="small"
                        buttonStyle="solid"
                    >
                        <Radio.Button value="all">
                            <FireFilled style={{ color: triggerType === 'all' ? '#fff' : '#f59e0b', marginRight: 4 }} />
                            Any Comment
                        </Radio.Button>
                        <Radio.Button value="specific">
                            <StarFilled style={{ color: triggerType === 'specific' ? '#fff' : '#8b5cf6', marginRight: 4 }} />
                            Specific Words
                        </Radio.Button>
                    </Radio.Group>
                </div>

                {triggerType === 'specific' && (
                    <div className="fao-trigger-words-input">
                        {/* Input with plus button */}
                        <div className="fao-trigger-input-wrapper">
                            <Input
                                placeholder="Type a word and press Enter"
                                value={triggerInput}
                                onChange={(e) => setTriggerInput(e.target.value)}
                                onKeyDown={handleTriggerInputKeyDown}
                                prefix={<MessageOutlined />}
                                suffix={
                                    <Button
                                        type="text"
                                        icon={<PlusOutlined />}
                                        onClick={addTriggerWord}
                                        disabled={!triggerInput.trim()}
                                        className="fao-add-word-btn"
                                    />
                                }
                            />
                        </div>

                        {/* Display trigger words as removable tags */}
                        {triggerWords.length > 0 && (
                            <div className="fao-trigger-tags">
                                {triggerWords.map((word, idx) => (
                                    <Tag
                                        key={idx}
                                        color="blue"
                                        closable
                                        onClose={() => removeTriggerWord(word)}
                                        className="fao-trigger-tag"
                                    >
                                        {word}
                                    </Tag>
                                ))}
                            </div>
                        )}

                        {triggerWords.length === 0 && (
                            <small className="fao-trigger-hint">
                                💡 Type a keyword and press Enter or click + to add
                            </small>
                        )}
                    </div>
                )}
            </div>

            {/* DM Message Section */}
            <div className="fao-config-section">
                <h4><SendOutlined /> DM Message</h4>

                {/* Quick Templates - show only if no category selected */}
                {!selectedCategory && (
                    <div className="fao-templates">
                        {messageTemplates.map((template, idx) => (
                            <div
                                key={idx}
                                className={`fao-template-chip ${dmMessage === template.value ? 'active' : ''}`}
                                onClick={() => setDmMessage(template.value)}
                            >
                                <span>{template.icon}</span>
                                <span>{template.label}</span>
                            </div>
                        ))}
                    </div>
                )}

                <Input.TextArea
                    placeholder="Paste link or add message"
                    value={dmMessage}
                    onChange={(e) => setDmMessage(e.target.value)}
                    rows={6}
                    className="fao-message-input"
                    maxLength={1000}
                    showCount
                />
            </div>
        </div>
    );

    const renderStep3 = () => (
        <div className="fao-step fao-step-test">
            <h2 className="fao-title">
                <span className="fao-step-number">3</span>
                Test Your Automation 🧪
            </h2>
            <p className="fao-subtitle">
                Your automation is now live! Let's test it to make sure everything works perfectly.
            </p>

            {/* Post Preview */}
            {selectedPost && (
                <div className="fao-test-post-preview">
                    <img
                        src={selectedPost.thumbnail_url || selectedPost.media_url || selectedPost.full_picture}
                        alt="Your post"
                        className="fao-test-post-image"
                    />
                </div>
            )}

            {/* Trigger Words Display */}
            <div className="fao-test-triggers">
                <h4>Comment one of these words:</h4>
                <div className="fao-test-trigger-tags">
                    {triggerType === 'all' ? (
                        <Tag color="green" className="fao-test-tag">Any comment will trigger</Tag>
                    ) : (
                        triggerWords.slice(0, 5).map((word, idx) => (
                            <Tag key={idx} color="blue" className="fao-test-tag">{word}</Tag>
                        ))
                    )}
                </div>
            </div>

            {/* Comment Tracking Section */}
            <div className={`fao-comment-tracker ${newSuccessDetected ? 'success' : ''}`}>
                <div className="fao-comment-tracker-header">
                    {newSuccessDetected ? (
                        <h4>✅ Automation Working!</h4>
                    ) : pollingStopped ? (
                        <h4>⏹️ Tracking Stopped</h4>
                    ) : (
                        <>
                            <h4>📡 Live Comment Tracking</h4>
                            {loadingComments && <Spin size="small" />}
                        </>
                    )}
                </div>

                {newSuccessDetected ? (
                    <div className="fao-success-message">
                        <CheckCircleFilled style={{ fontSize: 32, color: '#10b981' }} />
                        <p>Your automation is working perfectly!</p>
                        <small>New comments are being responded to automatically</small>
                    </div>
                ) : testComments.length === 0 ? (
                    <div className="fao-no-comments">
                        <div className="fao-waiting-animation">⏳</div>
                        <p>Waiting for comments...</p>
                        <small>Comment on your post from another account to test</small>
                    </div>
                ) : (
                    <div className="fao-comments-list">
                        {testComments.slice(0, 5).map((comment, idx) => (
                            <div key={comment.comment_id || idx} className={`fao-comment-item ${comment.reason ? 'has-reason' : ''}`}>
                                <div className="fao-comment-info">
                                    <span className="fao-comment-username">
                                        @{comment.event_core?.from?.username || 'user'}
                                    </span>
                                    <span className="fao-comment-text">
                                        "{comment.event_core?.text || 'Comment'}"
                                    </span>
                                </div>
                                <div className={`fao-comment-status ${comment.responded ? 'success' : comment.reason ? 'error' : 'waiting'}`}>
                                    {comment.responded ? (
                                        <>
                                            <CheckCircleFilled style={{ color: '#10b981' }} />
                                            <span>DM Sent!</span>
                                        </>
                                    ) : comment.reason ? (
                                        <>
                                            <span className="fao-comment-reason">⚠️ {comment.reason}</span>
                                        </>
                                    ) : (
                                        <>
                                            <Spin size="small" />
                                            <span>Sending...</span>
                                        </>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Instructions */}
            <div className="fao-test-instructions">
                <div className="fao-test-step">
                    <div className="fao-test-step-number">1</div>
                    <div className="fao-test-step-text">
                        <strong>Open Instagram</strong>
                        <span>Use your personal or second account</span>
                    </div>
                </div>
                <div className="fao-test-step">
                    <div className="fao-test-step-number">2</div>
                    <div className="fao-test-step-text">
                        <strong>Find your post</strong>
                        <span>Go to the post you just automated</span>
                    </div>
                </div>
                <div className="fao-test-step">
                    <div className="fao-test-step-number">3</div>
                    <div className="fao-test-step-text">
                        <strong>Comment a trigger word</strong>
                        <span>You'll receive a DM within seconds! ✨</span>
                    </div>
                </div>
            </div>

            <Button
                type="primary"
                size="large"
                className="fao-test-done-btn"
                onClick={() => setCurrentStep(4)}
            >
                {testComments.some(c => c.responded) ? "It's Working! Continue →" : "I've Tested It! Continue →"}
            </Button>
        </div>
    );

    const renderStep4 = () => (
        <div className="fao-step fao-step-success">
            <div className="fao-success-icon">
                <CheckCircleFilled />
            </div>
            <h2 className="fao-title">You're All Set! 🎉</h2>
            <p className="fao-subtitle">
                Congratulations! Your first automation is working.
                Every time someone comments, they'll automatically receive your DM!
            </p>

            {/* User Profile */}
            <div className="fao-user-profile">
                <img
                    src={decodedToken?.profile_picture_url || '/logo192.png'}
                    alt="Profile"
                    className="fao-user-avatar"
                />
                <span className="fao-user-name">@{decodedToken?.username || 'your_account'}</span>
            </div>

            <div className="fao-final-actions">
                <Button
                    type="primary"
                    size="large"
                    className="fao-setup-btn"
                    onClick={() => {
                        onComplete?.();
                        onSetupAccount?.();
                        onClose?.();
                    }}
                    icon={<RocketOutlined />}
                >
                    Setup your InstantDM Account
                </Button>
            </div>
        </div>
    );

    const canProceed = () => {
        switch (currentStep) {
            case 0: return true;
            case 1: return selectedPost !== null;
            case 2: return dmMessage.trim().length > 0 && (triggerType === 'all' || triggerWords.length > 0);
            default: return true;
        }
    };

    return (
        <Modal
            open={open}
            onCancel={currentStep === 4 ? onClose : undefined}
            closable={currentStep === 4}
            maskClosable={false}
            footer={null}
            width={720}
            className="fao-modal"
            centered
        >
            <div className="fao-container">
                {renderStepIndicator()}

                <div className="fao-content">
                    {currentStep === 0 && renderStep0()}
                    {currentStep === 1 && renderStep1()}
                    {currentStep === 2 && renderStep2()}
                    {currentStep === 3 && renderStep3()}
                    {currentStep === 4 && renderStep4()}
                </div>

                {/* Navigation Footer */}
                {currentStep > 0 && currentStep < 3 && (
                    <div className="fao-footer">
                        <Button
                            onClick={handleBack}
                            icon={<ArrowLeftOutlined />}
                        >
                            Back
                        </Button>

                        {currentStep === 2 ? (
                            <Button
                                type="primary"
                                onClick={handleCreateAutomation}
                                loading={creating}
                                disabled={!canProceed()}
                                icon={<ThunderboltOutlined />}
                                className="fao-automate-btn"
                            >
                                {creating ? 'Creating...' : 'Automate This Post'}
                            </Button>
                        ) : (
                            <Button
                                type="primary"
                                onClick={handleNext}
                                disabled={!canProceed()}
                                icon={<ArrowRightOutlined />}
                            >
                                Next
                            </Button>
                        )}
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default FirstAutomationOnboarding;
